import { HeroChart } from './HeroChart'
import { Link } from 'react-router-dom'
import { Section } from 'components/sto/Section'
import { ChartTabs } from 'components/ui/chart'
import { Fragment, type ReactNode } from 'react'
import { TokenIcon } from 'components/sto/chart'
import { NextDividend } from 'components/sto/Dividends'
import { ProductSwitcherChip } from './ProductSwitcher'
import { HeroMetrics } from 'components/sto/HeroMetrics'
import { Copy, DisplayOnBreakpoint, t } from '@stokr/components-library'
import { StatusChip, TypeChip } from 'components/ui/chip'
import { GOLD_CURRENCY } from 'models/productChart/fund'
import { useHeroIdentity, type HeroBreadcrumbItem, type HeroIdentity } from 'hooks/sto/useHeroIdentity'
import { useChartDenomination, usePickDenomination, useProductChart } from 'hooks/sto/useProductChart'
import {
  Title,
  TitleRow,
  BadgeRow,
  TitleBlock,
  Breadcrumb,
  Disclaimer,
  Description,
  MetricsSlot,
  BreadcrumbRow,
  TickerBadge,
  HeroContainer,
  IdentityBlock,
  DenominationSlot,
  BreadcrumbSeparator,
} from './Hero.styles'
import { useStoTrack } from 'hooks/sto/useStoTracking'

/**
 * Figma 3362:95631: XAUt / USD by the title, for a fund that reports in more than one currency
 * (Goldstream). Both its charts follow it. The chart data loads in the browser, so it appears then.
 */
const DenominationSwitch = ({ placement }: { placement: 'title' | 'badges' }) => {
  const state = useProductChart()
  const chart = state.status === 'ready' ? state.data.chart : undefined
  const pick = usePickDenomination()
  const active = useChartDenomination(chart)
  const trackSto = useStoTrack()
  if (!chart || !active || chart.denominations.length < 2) return null
  return (
    <DenominationSlot $placement={placement}>
      <ChartTabs<string>
        options={chart.denominations.map((item) => ({
          value: item.currency,
          // Codes upper-case (USD), gold as written (XAUt).
          label: item.currency === GOLD_CURRENCY ? item.currency : item.currency.toUpperCase(),
        }))}
        value={active.currency}
        onChange={(currency) => {
          if (currency !== active.currency) trackSto('sto_denomination_selected', { currency, placement })
          pick(currency)
        }}
        label={t('stoPage.performance.denominationLabel')}
        dataCy={`sto-denomination-${placement}`}
      />
    </DenominationSlot>
  )
}

/** The desktop badge-row ticker chip; on mobile it is the product switcher (ProductSwitcherChip). */
const TickerChip = ({ ticker, icons }: { ticker: string; icons: string[] }) => (
  <TickerBadge $placement="badges">
    {/* Keyed so moving to another project starts again from its best icon. */}
    <TokenIcon key={icons.join('|')} sources={icons} size={32} bare />
    {ticker}
  </TickerBadge>
)

/** Ticker, type and status chips: the hero's badge row. */
const IdentityChips = ({ identity, children }: { identity: HeroIdentity; children?: ReactNode }) => {
  const { ticker, tickerIcons, investmentType, status } = identity
  return (
    <BadgeRow>
      {ticker && <TickerChip ticker={ticker} icons={tickerIcons} />}
      <TypeChip type={investmentType} />
      <StatusChip color={status.color}>{status.label}</StatusChip>
      {children}
    </BadgeRow>
  )
}

const BreadcrumbLabel = ({ item }: { item: HeroBreadcrumbItem }) =>
  item.labelKey ? <Copy id={item.labelKey} /> : <>{item.label}</>

/**
 * "Invest / Equity Note / STRC Note". Its own component: in the hero band it runs above both
 * columns (Figma), while the identity below sits in the main column only.
 */
export const HeroBreadcrumb = () => {
  const state = useHeroIdentity()
  const trackSto = useStoTrack()
  if (state.status !== 'ready') return null
  const { ticker, tickerIcons } = state.data
  return (
    <BreadcrumbRow>
      <Breadcrumb aria-label="Breadcrumb">
        {state.data.breadcrumb.map((item, index) => (
          <Fragment key={item.to ?? item.label ?? index}>
            {index > 0 && <BreadcrumbSeparator aria-hidden="true">/</BreadcrumbSeparator>}
            {item.to ? (
              <Link
                to={item.to}
                data-cy={`sto-breadcrumb-${index}`}
                onClick={() => trackSto('sto_breadcrumb_clicked', { level: index, target: item.to })}>
                <BreadcrumbLabel item={item} />
              </Link>
            ) : (
              <span aria-current="page">
                <BreadcrumbLabel item={item} />
              </span>
            )}
          </Fragment>
        ))}
      </Breadcrumb>
      {ticker && <ProductSwitcherChip ticker={ticker} icons={tickerIcons} />}
    </BreadcrumbRow>
  )
}

/** The offering disclaimer under the hero: in the hero on desktop, below key terms on mobile. */
export const InvestDisclaimer = () => (
  <Disclaimer>
    <Copy id="common.investDisclaimerLabel" />
    <Copy id="common.investDisclaimerText" />
  </Disclaimer>
)

/** Product identity, hero metrics, the price card and the disclaimer. */
export const Hero = () => {
  const state = useHeroIdentity()

  if (state.status === 'hidden') return null
  if (state.status === 'loading') {
    return (
      <Section anchor="hero" state={state} variant="plain" skeletonRows={4}>
        {() => null}
      </Section>
    )
  }

  const { title, description } = state.data

  return (
    <Section anchor="hero" state={state} variant="plain">
      {() => (
        <HeroContainer>
          <IdentityBlock>
            <IdentityChips identity={state.data}>
              <DenominationSwitch placement="badges" />
            </IdentityChips>

            <TitleBlock>
              <TitleRow>
                <Title>{title}</Title>
                <DenominationSwitch placement="title" />
              </TitleRow>
              {description && <Description>{description}</Description>}
            </TitleBlock>

            <MetricsSlot>
              <HeroMetrics />
            </MetricsSlot>

            <NextDividend placement="hero" />
          </IdentityBlock>

          <HeroChart />

          {/* Mobile shows it after key terms instead (StoPage), as in Figma. */}
          <DisplayOnBreakpoint up breakpoint="MLarge">
            <InvestDisclaimer />
          </DisplayOnBreakpoint>
        </HeroContainer>
      )}
    </Section>
  )
}
